import React from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useFormik } from 'formik';
import api from '../../../../../components/api';
import { yupErrorMsg } from '../../../../../components/atoms/const';
import { State } from '../../../clients/add/hooks';

function usePartnersPartner(formikParent?: any) {
  const { partnerId } = useParams();

  // Checking where this form used on 'add' or 'edit' url
  const isEdit:boolean = React.useMemo(
    () => window.location.pathname.split('/')[2] === 'edit',
    [window.location.pathname],
  );

  // commission button switch condition handler
  const [commissionType, setCommissionType] = React.useState<1 | 2>(1);

  // Get Partner By Id from url params
  const { data: partnerRes, isLoading: partnerLoad, refetch } = useQuery(
    ['getById', 'partner', partnerId],
    () => api.partner.getById(partnerId!),
    { enabled: !!partnerId },
  );
  const partnerById = React.useMemo(() => partnerRes?.data?.data, [partnerRes]);

  // Get State List
  const { data: stateRes, isLoading: stateLoad } = useQuery(
    ['getAllStates', 'clients'],
    () => api.clients.getAllStates(),
  );
  const stateList: {value: string, label: string}[] = React.useMemo(() => {
    const resData: State[] | undefined = stateRes?.data?.data;
    if (resData) {
      return resData.map((state) => ({
        value: state.state,
        label: state.state,
      }));
    }
    return [];
  }, [stateRes]);

  // Update Partner
  const navigation = useNavigate();
  const { mutate: updatePartner, isLoading: updateLoad } = useMutation(api.partner.update, {
    onMutate: (variables) => console.log(variables),
    onError: (error) => console.log(error),
    onSuccess: (data) => {
      alert('success');
      if (isEdit) navigation(`/partners/${partnerId}`);
      if (!isEdit) refetch().catch((e) => console.log('err', e));
      console.log(data);
    },
  });

  const yupSchema = yup.object().shape({
    name: yup.string().required(yupErrorMsg.PARTNER_EMPTY),
    streetAddress: yup.string().required(yupErrorMsg.STREET_ADDRESS_EMPTY),
    zipCode: yup.number().required(yupErrorMsg.ZIP_CODE_EMPTY).test('len', yupErrorMsg.ZIP_CODE_INCORRECT_FORMAT, (val: any) => (val ? val.toString().length === 5 : false)),
    state: yup.string().required(yupErrorMsg.STATE_EMPTY),
    city: yup.string().required(yupErrorMsg.CITY_EMPTY),
    comission: yup.number().min(0, yupErrorMsg.COMMISION_POSITIVE).required(yupErrorMsg.COMMISION_EMPTY),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      streetAddress: '',
      zipCode: '',
      state: '',
      city: '',
      comission: 0,
    },
    validationSchema: yupSchema,
    onSubmit: (values) => {
      if (partnerId) {
        updatePartner({
          id: Number(partnerId),
          name: values.name,
          comission: values.comission,
          address: {
            streetAddress: values.streetAddress,
            zipCode: values.zipCode,
            city: values.city,
            state: values.state,
          },
        });
      }
    },
  });

  const currentFormik = React.useMemo(() => formikParent ?? formik, [formikParent, formik]);

  React.useEffect(() => {
    if (partnerById) {
      currentFormik.setFieldValue('name', partnerById.name ?? '');
      currentFormik.setFieldValue('streetAddress', partnerById.address?.streetAddress ?? '');
      currentFormik.setFieldValue('zipCode', partnerById.address?.zipCode ?? '');
      currentFormik.setFieldValue('state', partnerById.address?.state ?? '');
      currentFormik.setFieldValue('city', partnerById.address?.city ?? '');
      currentFormik.setFieldValue('comission', partnerById.comission ?? 0);
    }
  }, [partnerById]);

  return {
    commission: {
      current: commissionType,
      setCurrent: setCommissionType,
    },
    partner: {
      isLoading: partnerLoad,
      data: partnerById,
    },
    partnerId,
    formik: currentFormik,
    isEdit,
    updateLoad,
    state: {
      list: stateList,
      load: stateLoad,
    },
  };
}

export default usePartnersPartner;
